import { FunctionComponent, useEffect, useState } from "react";
import { useApiDispatch } from "@app/contexts/ApiDispatch";
import { apiGetDuplicates } from "@app/backendApi/actions";
import { ApiDuplicate } from "@app/backendApi/types";
import { useMountContext } from "./Mount";
import { Modal } from "./Modal";

/** Matches the server's own minimum; below it nothing is looked up. */
const MIN_QUERY = 3;

/**
 * How long typing has to pause before asking.
 *
 * Long enough that a name typed straight through costs one request rather than one per
 * letter, short enough that the link appears while the field still has attention.
 */
const SETTLE_MS = 250;

export type DuplicatesLinkProps = {
  /** What is currently in the name field. */
  name: string;
};

/**
 * "Possible duplicates", under the name field, when there are any.
 *
 * The row is always rendered and only its contents are hidden, so the form does not
 * grow by a line the moment a third character is typed — the Add button would step down
 * the screen under the cursor.
 */
export const DuplicatesLink: FunctionComponent<DuplicatesLinkProps> = ({
  name,
}) => {
  const apiDispatch = useApiDispatch();
  const mount = useMountContext();
  const [duplicates, setDuplicates] = useState<ApiDuplicate[]>([]);

  const query = name.trim();

  useEffect(() => {
    if (query.length < MIN_QUERY) {
      setDuplicates([]);
      return;
    }

    /*
     * Requests can land out of order — a longer name is not necessarily slower to
     * answer — so a reply that is no longer the current query is dropped rather than
     * shown. Without this, deleting a character can leave the results of the longer
     * name on screen.
     */
    let current = true;
    const timer = setTimeout(() => {
      apiDispatch(apiGetDuplicates(query))
        .then((found) => {
          if (current) setDuplicates(found);
        })
        .catch(() => {
          // A failed lookup is not worth interrupting somebody adding an item over.
          if (current) setDuplicates([]);
        });
    }, SETTLE_MS);

    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [query, apiDispatch]);

  const handleOpen = () => {
    const id = mount.push({
      element: (
        <Modal title="Possible duplicates">
          <ul className="flex flex-col gap-1">
            {duplicates.map((duplicate) => (
              <li
                key={duplicate.id}
                className="flex items-baseline justify-between gap-3 rounded-md
                  bg-surface-sunken px-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-ink">
                  {duplicate.name}
                </span>
                <span className="shrink-0 text-micro text-ink-muted tabular-nums">
                  {Math.round(duplicate.confidence * 100)}%
                </span>
              </li>
            ))}
          </ul>
        </Modal>
      ),
    });
    return id;
  };

  return (
    // `min-h` rather than conditional rendering: the row holds its line whether or not
    // there is anything to say.
    <div className="flex min-h-5 items-center">
      {duplicates.length > 0 ? (
        <button
          type="button"
          onClick={handleOpen}
          className="cursor-pointer border-b border-dashed border-ink-faint pb-px
            text-micro text-ink-muted transition-colors hover:border-accent
            hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2
            focus-visible:outline-accent"
        >
          Possible duplicates ({duplicates.length})
        </button>
      ) : null}
    </div>
  );
};
